import type { Condition } from '@databink/core/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import {
  Archive,
  ArchiveRestore,
  FileText,
  MoreHorizontal,
  Paperclip,
  Pencil,
  Plus,
} from 'lucide-react';
import { useRef, useState, type FormEvent } from 'react';
import { FieldInput, FieldValue, Money, StateBadge } from '../components/fields.js';
import { RecordForm } from '../components/record-form.js';
import { RecordTable } from '../components/record-table.js';
import {
  Button,
  buttonClass,
  Card,
  EmptyState,
  ErrorBanner,
  Label,
  Menu,
  MenuContent,
  MenuItem,
  MenuTrigger,
  Modal,
  NativeSelect,
  PageHeader,
  Spinner,
  Table,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  TD,
  Textarea,
  TH,
} from '../components/ui.js';
import type { RecordSlotProps, ScreenRecord } from '../extension-types.js';
import { formatDateTime, recordTitle } from '../lib/format.js';
import {
  childEntities,
  fieldOf,
  listColumns,
  sectionsFor,
  useAppMeta,
  useEntity,
  type AppEntity,
  type AppField,
  type AppMeta,
} from '../lib/meta.js';
import { useSlot } from '../lib/slots.js';
import { errorMessage, errorStatus, trpc, trpcClient } from '../lib/trpc.js';

type Slotted<P extends string> = `${'crm.party'}.${P}`;

/** Every entity's record screen. Tabs, actions and panels come from metadata; clients add their own through slots. */
export function RecordDetail() {
  const { entity: entityId, id } = useParams({ strict: false }) as { entity: string; id: string };
  const search = useSearch({ strict: false }) as { tab?: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meta = useAppMeta();
  const entity = useEntity(entityId);
  const record = useQuery(trpc.records.get.queryOptions({ entity: entityId, id }));
  const slotTabs = useSlot(`${entityId}.detail.tabs` as Slotted<'detail.tabs'>);
  const slotPanels = useSlot(`${entityId}.detail.panels` as Slotted<'detail.panels'>);
  const slotActions = useSlot(`${entityId}.detail.actions` as Slotted<'detail.actions'>);
  const [editing, setEditing] = useState(false);

  const refresh = () => void queryClient.invalidateQueries({ queryKey: trpc.records.pathKey() });

  if (record.isLoading) return <Spinner />;
  if (record.error || !record.data) {
    return (
      <ErrorBanner
        title={
          errorStatus(record.error) === 404
            ? `This ${entity.label.toLowerCase()} does not exist`
            : `Could not open this ${entity.label.toLowerCase()}`
        }
      >
        {errorMessage(record.error)}
      </ErrorBanner>
    );
  }
  const data = record.data as ScreenRecord;
  const props: RecordSlotProps = { record: data, entity, refresh };
  const children = childEntities(meta, entity);
  const tabs = slotTabs.filter((t) => !t.when || t.when(data));
  // Locked states (an issued invoice) and archived records are read-only; offering Edit would only lead to a refusal.
  const frozen =
    typeof data['archived_at'] === 'string' ||
    (typeof data['state'] === 'string' && Boolean(entity.states[data['state']]?.locked));
  const tab = search.tab ?? 'details';
  const setTab = (next: string) =>
    void navigate({
      to: '/e/$entity/$id',
      params: { entity: entityId, id },
      search: next === 'details' ? {} : { tab: next },
      replace: true,
    });

  return (
    <>
      <PageHeader
        eyebrow={
          <Link to="/e/$entity" params={{ entity: entityId }} className="hover:underline">
            {entity.plural}
          </Link>
        }
        title={
          <span className="flex flex-wrap items-center gap-x-3 gap-y-1 sm:flex-nowrap">
            <span className="min-w-0 sm:truncate">{recordTitle(entity, data)}</span>
            <StateBadge entity={entity} state={data['state']} />
            {typeof data['archived_at'] === 'string' && (
              <span className="text-sm font-normal text-slate-500">Archived</span>
            )}
          </span>
        }
        actions={
          <>
            {slotActions
              .filter((a) => !a.when || a.when(data))
              .map((a) => (
                <a.component key={a.id} {...props} />
              ))}
            <ActionButtons entity={entity} record={data} onDone={refresh} />
            {entity.templates.length > 0 && <DocumentButton entity={entity} record={data} />}
            {entity.access.update && !editing && !frozen && (
              <Button onClick={() => setEditing(true)}>
                <Pencil className="size-4" aria-hidden /> Edit
              </Button>
            )}
            <MoreMenu entity={entity} record={data} onDone={refresh} />
          </>
        }
      />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              {tabs.map((t) => (
                <TabsTrigger key={t.id} value={`x-${t.id}`}>
                  {t.label}
                </TabsTrigger>
              ))}
              {children.map((c) => (
                <TabsTrigger key={c.id} value={c.id}>
                  {childLabel(entity, c)}
                </TabsTrigger>
              ))}
              <TabsTrigger value="related">Related</TabsTrigger>
              <TabsTrigger value="comments">Comments</TabsTrigger>
              <TabsTrigger value="files">Files</TabsTrigger>
              <TabsTrigger value="history">History</TabsTrigger>
            </TabsList>
            <TabsContent value="details">
              {editing ? (
                <Card>
                  <RecordForm
                    entity={entity}
                    record={data}
                    onSaved={() => setEditing(false)}
                    onCancel={() => setEditing(false)}
                  />
                </Card>
              ) : (
                <Details meta={meta} entity={entity} record={data} />
              )}
              {entity.lines && Array.isArray(data['lines']) && (
                <Lines meta={meta} entity={entity} record={data} />
              )}
            </TabsContent>
            {tabs.map((t) => (
              <TabsContent key={t.id} value={`x-${t.id}`}>
                <t.component {...props} />
              </TabsContent>
            ))}
            {children.map((c) => (
              <TabsContent key={c.id} value={c.id}>
                <Children parent={entity} parentRecord={data} entity={c} />
              </TabsContent>
            ))}
            <TabsContent value="related">
              {tab === 'related' && <Related meta={meta} entity={entity} record={data} />}
            </TabsContent>
            <TabsContent value="comments">
              {tab === 'comments' && <Comments entity={entity} record={data} />}
            </TabsContent>
            <TabsContent value="files">
              {tab === 'files' && <Files entity={entity} record={data} />}
            </TabsContent>
            <TabsContent value="history">
              {tab === 'history' && <History entity={entity} record={data} />}
            </TabsContent>
          </Tabs>
        </div>
        <aside className="space-y-5">
          {slotPanels
            .filter((p) => !p.when || p.when(data))
            .map((p) => (
              <Card key={p.id} title={p.title}>
                <p.component {...props} />
              </Card>
            ))}
          <Card title="Record">
            <dl className="space-y-2 text-sm">
              {['owner_id', 'created_at', 'created_by', 'updated_at', 'updated_by'].map((key) => {
                const field = fieldOf(entity, key);
                return field ? (
                  <div key={key} className="flex justify-between gap-3">
                    <dt className="text-slate-500">{field.label}</dt>
                    <dd className="text-right text-slate-800">
                      <FieldValue entity={entity} field={field} record={data} />
                    </dd>
                  </div>
                ) : null;
              })}
            </dl>
          </Card>
        </aside>
      </div>
    </>
  );
}

function childLabel(parent: AppEntity, child: AppEntity): string {
  // "Party roles" on a party reads better as "Roles".
  const prefix = `${parent.label} `;
  return child.plural.startsWith(prefix)
    ? child.plural.slice(prefix.length).replace(/^./, (c) => c.toUpperCase())
    : child.plural;
}

function Details({
  meta,
  entity,
  record,
}: {
  meta: AppMeta;
  entity: AppEntity;
  record: ScreenRecord;
}) {
  const sections = sectionsFor(meta, entity).map((s) => ({
    ...s,
    fields: s.fields.filter((f) => f.key !== 'state'),
  }));
  return (
    <div className="space-y-5">
      {sections
        .filter((s) => s.fields.length > 0)
        .map((section) => (
          <Card key={section.label} title={section.label}>
            <dl className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
              {section.fields.map((field) => (
                <div key={field.key} className={field.type === 'long_text' ? 'sm:col-span-2' : ''}>
                  <dt className="text-xs font-medium text-slate-500">
                    {field.label}
                    {field.custom && <span className="sr-only"> (custom field)</span>}
                  </dt>
                  <dd className="mt-0.5 text-sm text-slate-900">
                    <FieldValue entity={entity} field={field} record={record} />
                  </dd>
                </div>
              ))}
            </dl>
          </Card>
        ))}
    </div>
  );
}

function Lines({
  meta,
  entity,
  record,
}: {
  meta: AppMeta;
  entity: AppEntity;
  record: ScreenRecord;
}) {
  const lineEntity = meta.entities.find((e) => e.id === entity.lines);
  if (!lineEntity) return null;
  const lines = (record['lines'] as ScreenRecord[]).map((l) => ({
    ...l,
    $titles: { ...(record.$titles ?? {}), ...(l.$titles ?? {}) },
  }));
  const keys = [
    'position',
    'description',
    'quantity',
    'unit_price',
    'discount_percent',
    'tax_rate',
    'line_total',
  ];
  const columns = keys.map((k) => fieldOf(lineEntity, k)).filter((f): f is AppField => Boolean(f));
  const currency = String(record['currency'] ?? meta.organization.base_currency);
  const totals = ['subtotal', 'tax_total', 'total', 'amount_paid', 'amount_due']
    .map((k) => fieldOf(entity, k))
    .filter((f): f is AppField => Boolean(f));
  return (
    <Card title="Lines" className="mt-5" padded={false}>
      <Table stack>
        <thead>
          <tr>
            {columns.map((c) => (
              <TH key={c.key} align={['description'].includes(c.key) ? 'left' : 'right'}>
                {c.key === 'position' ? '#' : c.label}
              </TH>
            ))}
          </tr>
        </thead>
        <tbody>
          {lines.map((line) => (
            <tr key={line.id}>
              {columns.map((c) => (
                <TD
                  key={c.key}
                  align={c.key === 'description' ? 'left' : 'right'}
                  label={c.label}
                  primary={c.key === 'description'}
                >
                  <FieldValue entity={lineEntity} field={c} record={{ currency, ...line }} />
                </TD>
              ))}
            </tr>
          ))}
        </tbody>
      </Table>
      <dl className="ml-auto w-full max-w-xs space-y-1 p-4 text-sm">
        {totals.map((f) => (
          <div
            key={f.key}
            className={
              f.key === 'total' || f.key === 'amount_due'
                ? 'flex justify-between font-semibold'
                : 'flex justify-between text-slate-600'
            }
          >
            <dt>{f.label}</dt>
            <dd>
              <Money value={record[f.key]} currency={currency} />
            </dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

function ActionButtons({
  entity,
  record,
  onDone,
}: {
  entity: AppEntity;
  record: ScreenRecord;
  onDone: () => void;
}) {
  const [pending, setPending] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const perform = useMutation({
    mutationFn: ({ action, input }: { action: string; input?: Record<string, unknown> }) =>
      trpcClient.records.perform.mutate({
        entity: entity.id,
        id: record.id,
        action,
        ...(input !== undefined && { input }),
      }),
    onSuccess: () => {
      setPending(null);
      setError(null);
      onDone();
    },
    onError: (e) => setError(e),
  });
  const state = typeof record['state'] === 'string' ? record['state'] : null;
  const available = entity.access.actions.filter((key) => {
    const action = entity.actions[key];
    return action && state !== null && action.from.includes(state) && !action.deprecated;
  });
  if (available.length === 0) return null;
  const chosen = pending ? entity.actions[pending] : undefined;
  const needsDialog = (key: string) =>
    Boolean(entity.actions[key]?.input || entity.actions[key]?.confirm);
  return (
    <>
      {available.map((key, i) => (
        <Button
          key={key}
          variant={i === 0 ? 'primary' : 'secondary'}
          busy={perform.isPending && perform.variables?.action === key}
          onClick={() => (needsDialog(key) ? setPending(key) : perform.mutate({ action: key }))}
          title={entity.actions[key]?.help}
        >
          {entity.actions[key]?.label ?? key}
        </Button>
      ))}
      {error !== null && !pending && (
        <Modal
          open
          onOpenChange={() => setError(null)}
          title="That could not be done"
          footer={<Button onClick={() => setError(null)}>Close</Button>}
        >
          <p className="text-sm text-slate-700">{errorMessage(error)}</p>
        </Modal>
      )}
      {pending && chosen && (
        <ActionDialog
          label={chosen.label}
          {...(chosen.confirm !== undefined && { confirm: chosen.confirm })}
          schema={chosen.input as JsonSchema | undefined}
          busy={perform.isPending}
          error={error}
          onCancel={() => {
            setPending(null);
            setError(null);
          }}
          onConfirm={(input) => perform.mutate({ action: pending, ...(input && { input }) })}
        />
      )}
    </>
  );
}

interface JsonSchema {
  properties?: Record<
    string,
    { type?: string; format?: string; description?: string; minLength?: number }
  >;
  required?: string[];
}

/** The field an action's input is entered with: a date picker for a date, which sends it as ISO. */
function inputType(key: string, prop: NonNullable<JsonSchema['properties']>[string]): string {
  if (prop.type !== 'string') return 'decimal';
  if (prop.format === 'date') return 'date';
  if (prop.format === 'date-time') return 'datetime';
  return key === 'reason' ? 'long_text' : 'text';
}

/** An action's input, drawn from its JSON Schema: most actions take a reason or an amount at most. */
function ActionDialog({
  label,
  confirm,
  schema,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  label: string;
  confirm?: string;
  schema: JsonSchema | undefined;
  busy: boolean;
  error: unknown;
  onCancel: () => void;
  onConfirm: (input?: Record<string, unknown>) => void;
}) {
  const properties = Object.entries(schema?.properties ?? {});
  const [values, setValues] = useState<Record<string, string>>({});
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (properties.length === 0) return onConfirm();
    const input: Record<string, unknown> = {};
    for (const [key, prop] of properties) {
      const value = values[key];
      if (value === undefined || value === '') continue;
      input[key] = prop.type === 'number' || prop.type === 'integer' ? Number(value) : value;
    }
    onConfirm(input);
  };
  return (
    <Modal open onOpenChange={(open) => !open && onCancel()} title={label} description={confirm}>
      <form onSubmit={submit} className="space-y-4">
        {error !== null && <ErrorBanner>{errorMessage(error)}</ErrorBanner>}
        {properties.map(([key, prop]) => {
          const field = {
            key,
            type: inputType(key, prop),
            label: key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
          } as AppField;
          return (
            <div key={key}>
              <Label htmlFor={`action-${key}`} required={schema?.required?.includes(key) ?? false}>
                {field.label}
              </Label>
              {field.type === 'long_text' ? (
                <Textarea
                  id={`action-${key}`}
                  value={values[key] ?? ''}
                  onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                />
              ) : (
                <FieldInput
                  field={field}
                  id={`action-${key}`}
                  value={values[key] ?? ''}
                  record={values}
                  onChange={(v) => setValues({ ...values, [key]: String(v ?? '') })}
                />
              )}
              {prop.description && (
                <p className="mt-1 text-xs text-slate-500">{prop.description}</p>
              )}
            </div>
          );
        })}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" busy={busy}>
            {label}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function DocumentButton({ entity, record }: { entity: AppEntity; record: ScreenRecord }) {
  const [open, setOpen] = useState(false);
  const [template, setTemplate] = useState(
    entity.templates.find((t) => t.default)?.key ?? entity.templates[0]?.key ?? '',
  );
  const base = `/downloads/documents/${encodeURIComponent(entity.id)}/${encodeURIComponent(record.id)}?template=${encodeURIComponent(template)}`;
  return (
    <>
      <Button onClick={() => setOpen(true)}>
        <FileText className="size-4" aria-hidden /> Document
      </Button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title={`${recordTitle(entity, record)}: document`}
        wide
        footer={
          <>
            {entity.templates.length > 1 && (
              <NativeSelect
                aria-label="Template"
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                className="mr-auto w-auto"
              >
                {entity.templates.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.label}
                  </option>
                ))}
              </NativeSelect>
            )}
            <a href={base} className={buttonClass('secondary')}>
              Download
            </a>
            <Button variant="primary" onClick={() => setOpen(false)}>
              Close
            </Button>
          </>
        }
      >
        {open && (
          <iframe
            title="Document preview"
            src={`${base}&disposition=inline`}
            sandbox="allow-same-origin"
            className="h-[65vh] w-full rounded border border-slate-200 bg-white"
          />
        )}
      </Modal>
    </>
  );
}

function MoreMenu({
  entity,
  record,
  onDone,
}: {
  entity: AppEntity;
  record: ScreenRecord;
  onDone: () => void;
}) {
  const archived = typeof record['archived_at'] === 'string';
  const toggle = useMutation({
    mutationFn: () =>
      archived
        ? trpcClient.records.restore.mutate({ entity: entity.id, id: record.id })
        : trpcClient.records.archive.mutate({ entity: entity.id, id: record.id }),
    onSuccess: onDone,
  });
  const allowed = archived ? entity.access.restore : entity.access.archive;
  if (!allowed) return null;
  return (
    <Menu>
      <MenuTrigger className={buttonClass('ghost')} aria-label="More actions">
        <MoreHorizontal className="size-4" />
      </MenuTrigger>
      <MenuContent>
        <MenuItem onSelect={() => toggle.mutate()}>
          {archived ? (
            <ArchiveRestore className="size-4" aria-hidden />
          ) : (
            <Archive className="size-4" aria-hidden />
          )}
          {archived ? 'Restore' : 'Archive'}
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

function Children({
  parent,
  parentRecord,
  entity,
}: {
  parent: AppEntity;
  parentRecord: ScreenRecord;
  entity: AppEntity;
}) {
  const meta = useAppMeta();
  const [adding, setAdding] = useState(false);
  const field = entity.parent?.field ?? '';
  const list = useQuery(
    trpc.records.list.queryOptions({
      entity: entity.id,
      filter: [{ field, op: 'eq', value: parentRecord.id }],
      limit: 100,
    }),
  );
  const columns = listColumns(meta, entity).filter((c) => c.key !== field);
  return (
    <Card
      title={childLabel(parent, entity)}
      padded={false}
      actions={
        entity.access.create && (
          <Button size="sm" onClick={() => setAdding(true)}>
            <Plus className="size-4" aria-hidden /> Add
          </Button>
        )
      }
    >
      {list.isLoading ? (
        <Spinner />
      ) : (
        <RecordTable
          entity={entity}
          records={(list.data?.records ?? []) as ScreenRecord[]}
          titles={list.data?.titles ?? {}}
          columns={columns}
        />
      )}
      <Modal
        open={adding}
        onOpenChange={setAdding}
        title={`Add ${entity.label.toLowerCase()}`}
        wide
      >
        {adding && (
          <RecordForm
            entity={entity}
            defaults={{ [field]: parentRecord.id }}
            onSaved={() => setAdding(false)}
            onCancel={() => setAdding(false)}
          />
        )}
      </Modal>
    </Card>
  );
}

/** Records elsewhere that point at this one: a customer's opportunities and invoices, an opportunity's quotes. */
function Related({
  meta,
  entity,
  record,
}: {
  meta: AppMeta;
  entity: AppEntity;
  record: ScreenRecord;
}) {
  const sources = meta.entities
    .filter((e) => e.parent === null && e.id !== entity.id)
    .flatMap((e) =>
      e.fields
        .filter((f) => f.type === 'reference' && f.reference === entity.id && !f.convention)
        .slice(0, 1)
        .map((f) => ({ entity: e, field: f })),
    );
  if (sources.length === 0) return <EmptyState title="Nothing else refers to this record" />;
  return (
    <div className="space-y-5">
      {sources.map(({ entity: e, field }) => (
        <RelatedList
          key={e.id}
          entity={e}
          filter={[{ field: field.key, op: 'eq', value: record.id }]}
        />
      ))}
    </div>
  );
}

function RelatedList({ entity, filter }: { entity: AppEntity; filter: Condition[] }) {
  const meta = useAppMeta();
  const list = useQuery(trpc.records.list.queryOptions({ entity: entity.id, filter, limit: 10 }));
  if (list.isLoading || (list.data?.total ?? 0) === 0) return null;
  return (
    <Card title={`${entity.plural} (${list.data?.total ?? 0})`} padded={false}>
      <RecordTable
        entity={entity}
        records={(list.data?.records ?? []) as ScreenRecord[]}
        titles={list.data?.titles ?? {}}
        columns={listColumns(meta, entity).slice(0, 5)}
      />
    </Card>
  );
}

function Comments({ entity, record }: { entity: AppEntity; record: ScreenRecord }) {
  const meta = useAppMeta();
  const queryClient = useQueryClient();
  const [body, setBody] = useState('');
  const options = trpc.comments.list.queryOptions({ entity: entity.id, id: record.id });
  const list = useQuery(options);
  const add = useMutation({
    mutationFn: () => trpcClient.comments.add.mutate({ entity: entity.id, id: record.id, body }),
    onSuccess: async () => {
      setBody('');
      await queryClient.invalidateQueries({ queryKey: options.queryKey });
    },
  });
  return (
    <Card title="Comments">
      <ul className="mb-4 space-y-3">
        {(list.data ?? []).length === 0 && (
          <li className="text-sm text-slate-500">No comments yet.</li>
        )}
        {(list.data ?? []).map((c) => (
          <li key={c.id} className="rounded-md bg-slate-50 px-3 py-2">
            <p className="text-xs text-slate-500">
              <span className="font-medium text-slate-700">{c.author_name ?? 'Someone'}</span> ·{' '}
              {formatDateTime(c.created_at)}
            </p>
            <p className="mt-1 text-sm whitespace-pre-wrap text-slate-800">{c.body}</p>
          </li>
        ))}
      </ul>
      {meta.access.writable && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (body.trim()) add.mutate();
          }}
          className="space-y-2"
        >
          {add.error && <ErrorBanner>{errorMessage(add.error)}</ErrorBanner>}
          <Label htmlFor="comment">Add a comment</Label>
          <Textarea id="comment" value={body} onChange={(e) => setBody(e.target.value)} />
          <div className="flex justify-end">
            <Button type="submit" variant="primary" busy={add.isPending} disabled={!body.trim()}>
              Comment
            </Button>
          </div>
        </form>
      )}
    </Card>
  );
}

function Files({ entity, record }: { entity: AppEntity; record: ScreenRecord }) {
  const meta = useAppMeta();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const options = trpc.attachments.list.queryOptions({ entity: entity.id, id: record.id });
  const list = useQuery(options);
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const response = await fetch(
        `/files/attachments?entity=${encodeURIComponent(entity.id)}&record_id=${encodeURIComponent(record.id)}`,
        {
          method: 'POST',
          headers: {
            'content-type': 'application/octet-stream',
            'x-filename': encodeURIComponent(file.name),
            'x-content-type': file.type || 'application/octet-stream',
            'x-databink-csrf': '1',
          },
          body: file,
        },
      );
      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as { message?: string } | null;
        throw new Error(body?.message ?? `The upload failed (${response.status})`);
      }
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: options.queryKey }),
  });
  return (
    <Card
      title="Files"
      padded={false}
      actions={
        // A read-only deploy takes no new files.
        meta.access.writable && (
          <>
            <input
              ref={input}
              type="file"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) upload.mutate(file);
                e.target.value = '';
              }}
            />
            <Button size="sm" busy={upload.isPending} onClick={() => input.current?.click()}>
              <Paperclip className="size-4" aria-hidden /> Attach a file
            </Button>
          </>
        )
      }
    >
      {upload.error && (
        <div className="p-3">
          <ErrorBanner>{errorMessage(upload.error)}</ErrorBanner>
        </div>
      )}
      {(list.data ?? []).length === 0 ? (
        <EmptyState title="No files" />
      ) : (
        <Table stack>
          <thead>
            <tr>
              <TH>File</TH>
              <TH align="right">Size</TH>
              <TH>Added</TH>
            </tr>
          </thead>
          <tbody>
            {(list.data ?? []).map((f) => (
              <tr key={f.id}>
                <TD primary>
                  <a
                    href={`/files/attachments/${f.id}`}
                    className="font-medium text-brand-strong hover:underline"
                  >
                    {f.filename}
                  </a>
                </TD>
                <TD align="right" label="Size">
                  {f.bytes < 1024 * 1024
                    ? `${Math.max(1, Math.round(f.bytes / 1024))} KB`
                    : `${(f.bytes / 1024 / 1024).toFixed(1)} MB`}
                </TD>
                <TD label="Added">{formatDateTime(f.created_at)}</TD>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Card>
  );
}

function History({ entity, record }: { entity: AppEntity; record: ScreenRecord }) {
  const meta = useAppMeta();
  const history = useQuery(trpc.records.history.queryOptions({ entity: entity.id, id: record.id }));
  if (history.isLoading) return <Spinner />;
  const show = (value: unknown) =>
    value === null || value === undefined
      ? ''
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);
  return (
    <Card title="History" padded={false}>
      <Table stack>
        <thead>
          <tr>
            <TH>When</TH>
            <TH>Who</TH>
            <TH>What</TH>
          </tr>
        </thead>
        <tbody>
          {(history.data ?? []).map((row) => {
            const field = row.field ? fieldOf(entity, row.field) : undefined;
            return (
              <tr key={row.id}>
                <TD primary className="whitespace-nowrap text-slate-500">
                  {formatDateTime(row.at, meta.organization.timezone)}
                </TD>
                <TD label="Who">
                  {row.actor_name ?? 'The system'}
                  {row.source !== 'ui' && (
                    <span className="ml-1 text-xs text-slate-400">({row.source})</span>
                  )}
                </TD>
                <TD label="What">
                  {field ? (
                    <>
                      <span className="font-medium">{field.label}</span>:{' '}
                      {show(row.old_value) || 'empty'} → {show(row.new_value) || 'empty'}
                    </>
                  ) : (
                    <span className="capitalize">{row.action.replace(/_/g, ' ')}</span>
                  )}
                </TD>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}
